import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, from, throwError } from 'rxjs';
import { AuthService, clearLegacyAuthCookie } from '../services/auth.service';
import { catchError, mergeMap } from 'rxjs/operators';
import { LicenseService } from '../services/license.service';
import { environment } from '../../environments';
import { isRejectedTokenChallenge } from '../services/identity-session';
import { freshTokenOutcome, unauthorizedAction } from './unauthorized';

export const SESSION_ERROR_ROUTE = '/session-error'

function isUnauthorized(err: unknown): err is HttpErrorResponse {
    return err instanceof HttpErrorResponse && err.status === 401
}

function isRejectedToken(err: HttpErrorResponse): boolean {
    return isRejectedTokenChallenge(err.headers?.get('WWW-Authenticate') ?? null)
}

@Injectable({
    providedIn: 'root'
})
export class HttpConfigInterceptor implements HttpInterceptor {
    constructor(
        public authService: AuthService,
        public licenseService: LicenseService,
        public router: Router,
    ) { }

    private isHandlingUnauthorized = false

    // Set when the API rejects a token identity has just issued. Further API
    // calls fail fast until the page reloads, so components cannot loop.
    private sessionRejected = false

    // The identity JWT goes to the Lobbyze API only, never to third parties.
    private isApiRequest(req: HttpRequest<unknown>): boolean {
        return req.url.startsWith(environment.API_URL)
    }

    private send(req: HttpRequest<any>, next: HttpHandler, token: string | null): Observable<HttpEvent<any>> {
        return next.handle(req.clone({
            // The selected-tier and tracking cookies still ride on API requests.
            withCredentials: true,
            setHeaders: token ? { Authorization: `Bearer ${token}` } : {},
        }))
    }

    /** A 401 for a token the API accepted is a permission or license answer. */
    private async handleUnauthorized(url: string | null, sentToken: boolean) {
        if (!sentToken) {
            console.error("[HTTP ERROR] no identity session - redirecting to sign-in", { url })
            clearLegacyAuthCookie()
            this.authService.login()
            return
        }
        if (this.isHandlingUnauthorized) {
            return
        }
        this.isHandlingUnauthorized = true
        try {
            const license = await this.licenseService.getLicense().catch(() => null)
            if (license && license.license === "") {
                this.router.navigate([license.self ? '/buy' : '/no-license'])
            }
        } finally {
            this.isHandlingUnauthorized = false
        }
    }

    private rejectSession(url: string | null) {
        if (this.sessionRejected) {
            return
        }
        this.sessionRejected = true
        console.error("[HTTP ERROR] API rejected a fresh identity token", { url })
        clearLegacyAuthCookie()
        this.router.navigateByUrl(SESSION_ERROR_ROUTE)
    }

    /** Retries a request once with a newly issued token. */
    private retryWithFreshToken(req: HttpRequest<any>, next: HttpHandler, original: HttpErrorResponse): Observable<HttpEvent<any>> {
        return from(this.authService.accessToken(true)).pipe(
            mergeMap((fresh) => {
                const outcome = freshTokenOutcome(fresh, this.authService.identityUnreachable)
                if (outcome === 'sign-in') {
                    void this.handleUnauthorized(original.url, false)
                    return throwError(original)
                }
                if (outcome === 'give-up') {
                    console.error("[HTTP ERROR] identity is unreachable; not retrying", { url: original.url })
                    return throwError(original)
                }
                return this.send(req, next, fresh).pipe(
                    catchError((err) => {
                        if (isUnauthorized(err)) {
                            const action = unauthorizedAction({ sentToken: true, rejectedToken: isRejectedToken(err), retried: true })
                            if (action === 'reject-session') {
                                this.rejectSession(err.url)
                            } else {
                                void this.handleUnauthorized(err.url, true)
                            }
                        }
                        return throwError(err)
                    })
                )
            })
        )
    }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (!this.isApiRequest(req)) {
            return next.handle(req)
        }
        if (this.sessionRejected) {
            return throwError(new HttpErrorResponse({ status: 401, statusText: 'Session rejected', url: req.url }))
        }
        return from(this.authService.accessToken()).pipe(
            mergeMap((token) => this.send(req, next, token).pipe(
                catchError((err) => {
                    if (!isUnauthorized(err)) {
                        return throwError(err)
                    }
                    const action = unauthorizedAction({ sentToken: !!token, rejectedToken: isRejectedToken(err), retried: false })
                    if (action === 'retry') {
                        return this.retryWithFreshToken(req, next, err)
                    }
                    void this.handleUnauthorized(err.url, action !== 'sign-in')
                    return throwError(err)
                })
            ))
        )
    }
}
